<template>
  <div>
    <VRow
      class="d-flex"
      :class="{
        'flex-column': $vuetify.breakpoint.smAndDown,
        'pl-6': $vuetify.breakpoint.smAndUp,
      }"
    >
      <VCol
        lg="3"
        md="3"
        sm="12"
        xs="12"
        class="d-flex align-items-center"
        :class="{
          'flex-column': $vuetify.breakpoint.mdAndUp,
          'pb-0': $vuetify.breakpoint.mdAndDown,
        }"
      >
        <div :class="{ 'w-100': $vuetify.breakpoint.mdAndUp }">
          <VIcon
            large
            class="checkout-icon"
            :class="{ 'pr-1': $vuetify.breakpoint.smAndDown }"
          >
            mdi-account
          </VIcon>
        </div>
        <div :class="{ 'w-100': $vuetify.breakpoint.mdAndUp }">
          <span class="body-bold">{{ $t("checkout.contact") }}</span>
        </div>
      </VCol>
      <VCol cols="12" md="8" sm="11" class="pb-0">
        <div v-if="customerDetails">
          <span
            class="body-1 flex-nowrap"
            v-html="$t('checkout.bookingAs', { name: getFullName })"
          />
          <div
            class="d-flex alignt-center mt-6"
            :class="{
              'pl-2': $vuetify.breakpoint.mdAndDown,
            }"
          >
            <VSwitch
              class="mt-0"
              data-test="anotherPassengerSwitch"
              :input-value="isAnotherPassenger"
              inset
              hide-details
              @change="updatePassenger"
            />
            <div class="d-flex align-items-center pb-5">
              <span class="inline-checkbox-label body-1">{{
                $t("checkout.anotherPerson")
              }}</span>
            </div>
          </div>
        </div>
        <SAuthForm
          v-else
          :isAuthPage="false"
          :isDisplayedImageAndTitle="false"
          :lang="lang"
          :location="userLocation"
          :termsAndConditionsPageName="termsAndConditionsPageName"
          :privacyPolicyPageName="privacyPolicyPageName"
          @populateUserProfileInfo="populateUserProfileInfo"
          @setAuthToken="setAuthToken"
          @setGlobalLoading="setGlobalLoading"
          @trackGtmEventForRegisteredUser="
            $emit('trackGtmEventForRegisteredUser')
          "
          @trackGtmEventForNonRegisteredUser="
            $emit('trackGtmEventForNonRegisteredUser')
          "
          @trackGtmEventForUserCreatedByOperator="
            $emit('trackGtmEventForUserCreatedByOperator')
          "
        />
      </VCol>
    </VRow>
    <template v-if="isAnotherPassenger">
      <AnotherPassengerDialogForm
        :isAnotherPassengerDialogShow="isAnotherPassengerDialogShow"
        :anotherPassengersList="anotherPassengersList"
        :anotherPassenger="anotherPassenger"
        :isEditMode="isEditMode"
        :searchQuery="searchQuery"
        :isItemSelected="isItemSelected"
        :country="opsZoneCountry"
        :customerPhone="customerDetails.phone"
        @updateLastEditedPassenger="updateLastEditedPassenger"
        @closeDialog="isAnotherPassengerDialogShow = false"
        @populateUserProfileInfo="populateUserProfileInfo"
      />
      <VRow
        v-if="anotherPassengersList.length"
        class="d-flex mt-6 pb-0"
        :class="$vuetify.breakpoint.xs ? 'flex-column' : 'pl-6'"
      >
        <VCol
          lg="3"
          md="3"
          sm="12"
          xs="12"
          cols="2"
          class="d-flex flex-column py-0"
        />
        <VCol
          class="d-flex py-0"
          :class="$vuetify.breakpoint.smAndDown ? 'flex-column' : ''"
          cols="12"
          md="9"
          sm="11"
        >
          <ValidationProvider
            ref="passenger"
            v-slot="{ errors, ariaInput }"
            name="passenger"
            tag="div"
            class="autocomplete-wrapper"
            :rules="{ required: true }"
            :allowFalse="false"
            mode="eager"
          >
            <VAutocomplete
              v-bind="ariaInput"
              ref="autocomplete"
              v-model="anotherPassenger"
              item-text="name"
              class="py-0"
              attach
              :error="!!errors.length"
              :error-messages="$t(errors[0])"
              :items="getAdaptedPassengers"
              :noDataText="
                $t('checkout.anotherPersonDetails.noMatchingResilts')
              "
              :label="$t('checkout.anotherPersonDetails.autocompleteLabel')"
              @change="(value) => changeAnotherPassenger(value)"
              @update:search-input="onSearchInput"
            >
              <template #append-item>
                <VListItem>
                  <VListItemContent class="py-0">
                    <VBtn
                      class="brandingPrimary--text header__link"
                      color="brandingPrimary--text"
                      :active="false"
                      text
                      @click="openDialog(false)"
                    >
                      <VIcon left> mdi-plus </VIcon>
                      <span class="button brandingPrimary--text">
                        {{ $t("checkout.anotherPersonDetails.addNewButton") }}
                      </span>
                    </VBtn>
                  </VListItemContent>
                </VListItem>
              </template>
            </VAutocomplete>
          </ValidationProvider>
          <VBtn
            v-if="isItemSelected"
            class="brandingPrimary--text header__link"
            color="brandingPrimary--text"
            :active="false"
            text
            @click="openDialog(true)"
          >
            <VIcon left> mdi-pencil </VIcon>
            <span class="button brandingPrimary--text">
              {{ $t("checkout.anotherPersonDetails.editButton") }}
            </span>
          </VBtn>
        </VCol>
      </VRow>
      <template v-else>
        <VRow
          class="d-flex pl-6 mt-12 pb-0"
          :class="{ 'flex-column': $vuetify.breakpoint.smAndDown }"
        >
          <VCol
            lg="3"
            md="3"
            sm="12"
            xs="12"
            cols="2"
            class="d-flex flex-column py-0"
          />
          <VCol class="py-0" cols="12" md="8" sm="11">
            <ValidationProvider
              ref="phone"
              v-slot="{ errors, ariaInput }"
              name="phone"
              :rules="phoneRules"
              :allowFalse="false"
              mode="eager"
            >
              <vue-tel-input-vuetify
                ref="phoneInput"
                v-bind="ariaInput"
                :value="typeof phone === 'string' ? phone : ''"
                v-phone-restrict
                data-test="passenger-phone-input"
                :loading="isLoadingCountries"
                :disabled="isCustomerPhoneUsed"
                class="body-1"
                :class="{ incorrect: !!errors.length }"
                :defaultCountry="opsZoneCountry || userLocation"
                disabledFetchingCountry
                :error="!!errors.length"
                :error-messages="$t(errors[0])"
                :placeholder="$t('authForm.enterPhoneNumber')"
                :mode="'international'"
                :label="$t('checkout.anotherPersonDetails.phone')"
                @input="onPhoneInput"
                @country-changed="countryChanged(false)"
              />
            </ValidationProvider>
          </VCol>
        </VRow>
        <VRow
          class="d-flex pl-6 mb-2 checkbox-wrapper"
          :class="{ 'flex-column': $vuetify.breakpoint.smAndDown }"
        >
          <VCol
            v-if="!$vuetify.breakpoint.smAndDown"
            lg="3"
            md="3"
            sm="12"
            xs="12"
            cols="2"
            class="d-flex flex-column py-0"
          />
          <VCol class="py-0" cols="12" md="8" sm="11">
            <VCheckbox
              v-model="isCustomerPhoneUsed"
              dense
              color="brandingPrimary"
              class="smaller-label body-2 mt-0 pb-0"
              data-test="use-my-phone-checkbox"
              @change="updatePhone"
            >
              <template #label>
                <span class="body-2">{{
                  $t("checkout.anotherPersonDetails.useMyPhoneNumber")
                }}</span>
              </template>
            </VCheckbox>
          </VCol>
        </VRow>
        <VRow
          class="d-flex pl-6 mt-0 pt-0 mb-6"
          :class="{ 'flex-column': $vuetify.breakpoint.smAndDown }"
        >
          <VCol
            v-if="!$vuetify.breakpoint.smAndDown"
            lg="3"
            md="3"
            sm="12"
            xs="12"
            class="d-flex flex-column"
          />
          <VCol cols="12" md="8" sm="11" class="py-0">
            <ValidationProvider
              ref="firstName"
              v-slot="{ errors, ariaInput }"
              name="firstName"
              :rules="{
                required: true,
                max: 255,
                atLeastTwoLetters: /[a-zA-Z]{2,}/,
                onlyLettersAndSymbols: firstSecondNameRegex,
              }"
              :allowFalse="false"
              mode="eager"
            >
              <VTextField
                v-bind="ariaInput"
                class="py-0 mt-0"
                data-test="passenger-first-name-input"
                :hide-details="!errors.length"
                :value="passengerFirstName"
                :error="!!errors.length"
                :error-messages="validationErrorResolve(errors[0])"
                :label="$t('checkout.anotherPersonDetails.firstName')"
                @input="updateFirstName"
              />
            </ValidationProvider>
          </VCol>
        </VRow>
        <VRow
          class="d-flex pl-6 mt-0"
          :class="{ 'flex-column': $vuetify.breakpoint.smAndDown }"
        >
          <VCol
            v-if="!$vuetify.breakpoint.smAndDown"
            lg="3"
            md="3"
            sm="12"
            xs="12"
            class="d-flex flex-column"
          />
          <VCol class="py-0" cols="12" md="8" sm="11">
            <ValidationProvider
              ref="lastName"
              v-slot="{ errors, ariaInput }"
              name="lastName"
              :rules="{
                required: true,
                max: 255,
                atLeastTwoLetters: /[a-zA-Z]{2,}/,
                onlyLettersAndSymbols: firstSecondNameRegex,
              }"
              :allowFalse="false"
              mode="eager"
            >
              <VTextField
                v-bind="ariaInput"
                class="pt-0 pb-4 mt-0"
                data-test="passenger-last-name-input"
                :hide-details="!errors.length"
                :value="passengerLastName"
                :error="!!errors.length"
                :error-messages="validationErrorResolve(errors[0])"
                :label="$t('checkout.anotherPersonDetails.lastName')"
                @input="updateLastName"
              />
            </ValidationProvider>
          </VCol>
        </VRow>
      </template>
      <VRow
        v-if="isNightCardsSupport && $slots.anotherPassengerNightCard"
        class="d-flex pl-6 mt-0 mb-6"
        :class="{ 'flex-column': $vuetify.breakpoint.smAndDown }"
      >
        <VCol
          v-if="!$vuetify.breakpoint.smAndDown"
          lg="3"
          md="3"
          sm="12"
          xs="12"
          class="d-flex flex-column"
        />
        <VCol class="py-0" cols="12" md="8" sm="11">
          <slot name="anotherPassengerNightCard" />
        </VCol>
      </VRow>
    </template>
  </div>
</template>

<script lang="ts">
export default {
  name: 'ContactInfo',
};
</script>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { SAuthForm } from '@slg/web-customer-shared';
import Constants from '@slg/web-customer-shared/src/utils/constants';
import { phoneFromTelInput } from '@slg/web-customer-shared/src/utils/telInput';
import AnotherPassengerDialogForm from '@slg/web-customer-shared/src/components/checkout/passengerInfo/anotherPassengerDialogForm/AnotherPassengerDialogForm.vue';
import { useValidationError, useVm } from '../../../../composables';

const props = defineProps({
  customerDetails: {
    type: Object,
    default: null,
  },
  authToken: {
    type: Boolean,
    default: null,
  },
  passengerPhone: {
    type: String,
    default: '',
  },
  opsZoneCountry: {
    type: String,
    default: '',
  },
  userLocation: {
    type: String,
    default: '',
  },
  termsAndConditionsPageName: {
    type: String,
    default: null,
  },
  privacyPolicyPageName: {
    type: String,
    default: null,
  },
  passengerFirstName: {
    type: String,
    default: '',
  },
  passengerLastName: {
    type: String,
    default: '',
  },
  passengerEmail: {
    type: String,
    default: '',
  },
  isAnotherPassenger: {
    type: Boolean,
    default: false,
  },
  lang: {
    type: String,
    default: null,
  },
  anotherPassengersList: {
    type: Array,
    default: () => [],
  },
  isNightCardsSupport: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'updatePhone',
  'updateFirstName',
  'updateLastName',
  'updatePassenger',
  'onSignInSuccess',
  'populateUserProfileInfo',
  'setAuthToken',
  'common/setGlobalLoading',
  'trackGtmEventForRegisteredUser',
  'trackGtmEventForNonRegisteredUser',
  'trackGtmEventForUserCreatedByOperator',
]);

const { validationErrorResolve } = useValidationError();
const vm = useVm();

const phoneInput = ref(null);
const autocomplete = ref(null);

const phone = ref(null);
const isValidPhoneNumber = ref(true);
const phoneRules = computed(() => ({
  required: true,
  phoneNumber: {
    isValidPhoneNumber: isValidPhoneNumber.value,
  },
}));
const firstSecondNameRegex = Constants.FIRST_SECOND_NAME_REGEX;
const isAnotherPassengerDialogShow = ref(false);
const anotherPassenger = ref(null);
const isEditMode = ref(true);
const isItemSelected = ref(true);
const searchQuery = ref('');
const lastUpdatedPassenger = ref('');
const defaultDialCode = ref('');
const isLoadingCountries = ref(false);
const isCustomerPhoneUsed = ref(false);

const getFullName = computed(() => `${props.passengerFirstName} ${props.passengerLastName}`);

const getPhoneObject = computed(() => phoneInput.value?.phoneObject);

function onPhoneInput(value: unknown) {
  phone.value = phoneFromTelInput(value);
}

const getAdaptedPassengers = computed(() => props.anotherPassengersList
  .map((passenger) => {
    return {
      value: passenger,
      name: `${passenger.firstName} ${passenger.lastName} (${passenger.phone})`,
    };
  })
  .sort((a, b) => a.value.firstName.localeCompare(b.value.firstName)));

async function validatePhone() {
  nextTick(async () => {
    isValidPhoneNumber.value = phoneInput.value?.phoneObject.isValid;
  });
}

async function countryChanged(isDefaultCountry = true) {
  if (props.isAnotherPassenger) {
    nextTick(async () => {
      isLoadingCountries.value = true;
      if (isDefaultCountry) {
        const defaultCountry = phoneInput.value?.allCountries.find(
          (country) => country.name.includes(vm.country)
        );
        defaultDialCode.value = defaultCountry?.dialCode;
        phone.value = defaultCountry ? `+${defaultDialCode.value}` : '+352';
      } else {
        phone.value = `+${phoneInput.value?.phoneObject?.country?.dialCode}`;
      }
      isLoadingCountries.value = false;
    });
  }
}

async function updatePhone(isCustomerPhoneUsedValue) {
  nextTick(async () => {
    if (isCustomerPhoneUsedValue) {
      phone.value = props.customerDetails.phone;
    } else {
      await countryChanged();
    }
    if (props.isAnotherPassenger) {
      emit('updatePhone', phone.value.replace(/\s/g, ''));
    }
  });
}

function updateFirstName(newValue) {
  emit('updateFirstName', newValue);
}

function updateLastName(newValue) {
  emit('updateLastName', newValue);
}

function changeAnotherPassenger(passenger) {
  anotherPassenger.value = passenger;
  emit('updateFirstName', passenger.firstName);
  emit('updateLastName', passenger.lastName);
  emit('updatePhone', passenger.phone);
}

async function updatePassenger(newValue) {
  if (newValue && props.anotherPassengersList.length) {
    emit('updatePassenger', newValue);
    await countryChanged();
    changeAnotherPassenger(props.anotherPassengersList[0]);
  } else {
    emit('updatePassenger', newValue);
    await countryChanged();
    await countryChanged();
  }
}

function onSignInSuccess() {
  emit('onSignInSuccess');
}

function populateUserProfileInfo() {
  emit('populateUserProfileInfo');
}

function setAuthToken(authToken) {
  emit('setAuthToken', authToken);
}

function setGlobalLoading(isLoading) {
  emit('common/setGlobalLoading', isLoading);
}

function clearAnotherPassenger() {
  anotherPassenger.value = null;
}

function onSearchInput() {
  isItemSelected.value =
    !autocomplete.value?.isSearching && !!anotherPassenger.value;
  searchQuery.value = autocomplete.value?.internalSearch;
}

function closeDialog() {
  isAnotherPassengerDialogShow.value = false;
}

function updateLastEditedPassenger(passengerId) {
  lastUpdatedPassenger.value = passengerId;
}

function openDialog(isEditModeValue) {
  isAnotherPassengerDialogShow.value = !isAnotherPassengerDialogShow.value;
  isEditMode.value = isEditModeValue;
}

watch(phone, async () => {
  if (props.isAnotherPassenger) {
    if (isCustomerPhoneUsed.value) {
      phone.value = props.customerDetails.phone;
      await validatePhone();
    }
    emit('updatePhone', phone.value?.replace(/\s/g, ''));
    await validatePhone();
    isCustomerPhoneUsed.value =
      phone.value?.replace(/\s/g, '') === props.customerDetails.phone;
  }
}, { deep: true, immediate: true });

watch(() => props.anotherPassengersList, async () => {
  if (!props.isAnotherPassenger) {
    updatePassenger(false);
  } else if (
    lastUpdatedPassenger.value &&
    props.anotherPassengersList.length
  ) {
    changeAnotherPassenger(
      props.anotherPassengersList.find(
        (passenger) => passenger.passengerId === lastUpdatedPassenger.value
      ) || props.anotherPassengersList[0]
    );
  } else if (
    !lastUpdatedPassenger.value &&
    props.anotherPassengersList.length
  ) {
    changeAnotherPassenger(props.anotherPassengersList[0]);
  } else {
    emit('updateFirstName', '');
    emit('updateLastName', '');
    await countryChanged();
    await countryChanged();
  }
}, { deep: true, immediate: true });
</script>
<style lang="scss" scoped>
.select {
  .v-list {
    padding: unset !important;
  }
}
.autocomplete-wrapper {
  width: 100%;
}

.vue-tel-input-vuetify {
  height: 18px !important;

  &.incorrect {
    margin-bottom: 16px;
  }
}
</style>
